import {AfterViewInit, ChangeDetectorRef, Component, Input, OnInit, QueryList, TemplateRef, ViewChildren,} from '@angular/core';
import {AuthControllerService, HtmlDocumentControllerService, HtmlDocumentDto, OrganizationMinimalDto, UserDto,} from '@api/utisha-backend';
import {TenantService} from '../services/tenant.service';
import {firstValueFrom, forkJoin} from 'rxjs';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {ActivatedRoute} from '@angular/router';
import {ResponsiveService} from 'src/app/services/responsive.service';
import {LanguageService} from 'src/app/services/language.service';
import {UserService, UtishaAuthProvider,} from 'src/app/services/user/user.service';
import {NavigationService} from 'src/app/services/nav/navigation.service';
import {UtishaGeneralStore} from 'src/app/services/storage/general-store';
import {CookieService} from 'src/app/services/cookie.service';
import {getCurrentUser, resetPassword} from '@aws-amplify/auth';
import {camelize} from '../util/string-utils';
import {TemplateNameDirective} from '../directives/templateName.directive';
import {tryResolveCognitoI18nError} from './utils/cognito-utils';
import {MessageService} from '../services/message/message.service';
import {NgcCookieConsentService} from 'ngx-cookieconsent';
import {NgTemplateOutlet} from '@angular/common';
import {LanguageSwitchComponent} from '../language-switch/language-switch.component';
import {S3ImgComponent} from '../aws/s3-img/s3-img.component';
import {LoginChoiceComponent} from './login-choice/login-choice.component';
import {UtButtonComponent, UtIconComponent, UtInputComponent} from '../ui';
import {FormsModule} from '@angular/forms';
import {ChangePasswordComponent} from './change-password/change-password.component';
import {NgScrollbar} from 'ngx-scrollbar';
import {logUtishaDebug, logUtishaError, logUtishaInfo} from '../util/log';
import UserTypeEnum = UserDto.UserTypeEnum;

type PageType =
  | 'auth-method'
  | 'document'
  | 'email'
  | 'account-created'
  | 'existing-user-password'
  | 'new-user-password'
  | 'signup'
  | 'signup-success'
  | 'unconfirmed'
  | 'confirmed'
  | 'logged-in'
  | 'forgot-password'
  | 'forgot-password-submitted'
  | 'reset-password'
  | 'reset-password-success'
  | 'error';

const EMAIL_REGEX = '[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}';

/**
 * WeChat oauth login - https://developers.weixin.qq.com/doc/oplatform/en/Website_App/WeChat_Login/Wechat_Login.html
 */
@Component({
  selector: 'app-login-v2',
  templateUrl: './login-v2.component.html',
  styleUrls: ['./login-v2.component.scss'],
  imports: [
    LanguageSwitchComponent,
    NgTemplateOutlet,
    S3ImgComponent,
    TemplateNameDirective,
    LoginChoiceComponent,
    UtInputComponent,
    FormsModule,
    UtButtonComponent,
    ChangePasswordComponent,
    UtIconComponent,
    NgScrollbar,
    TranslatePipe,
  ],
})
export class LoginV2Component implements OnInit, AfterViewInit {
  @ViewChildren(TemplateNameDirective)
  templates!: QueryList<TemplateNameDirective>;
  private templateMap: Map<string, TemplateRef<any>> = new Map();
  pageTemplate: TemplateRef<any> | undefined;
  provider: string;
  email: string | undefined;
  name: string;
  org: OrganizationMinimalDto | null = null;
  privacyStatement: HtmlDocumentDto;
  conditionsStatement: HtmlDocumentDto;
  selectedConditionsDocument: HtmlDocumentDto;

  @Input() page: PageType = 'auth-method';
  @Input() accountCreated: boolean = false;

  error: string | undefined;
  password: string | undefined;
  loading = true;
  private campaign: string;
  private confirmed: boolean;
  user: UserDto;

  constructor(
    public responsive: ResponsiveService,
    private tenantService: TenantService,
    private htmlDocSevice: HtmlDocumentControllerService,
    private languageService: LanguageService,
    private translate: TranslateService,
    private authService: AuthControllerService,
    private userServiceFrontend: UserService,
    public nav: NavigationService,
    private changeDetector: ChangeDetectorRef,
    private cookieService: CookieService,
    private utishaGeneralStore: UtishaGeneralStore,
    private messageService: MessageService,
    private ccService: NgcCookieConsentService,
    route: ActivatedRoute,
  ) {
    this.page = 'auth-method';
    this.confirmed = route.snapshot.queryParams['confirmed'] === 'true';
    const referer = route.snapshot.queryParams['referer'];
    if (referer) {
      this.utishaGeneralStore.setReferer(referer);
    }
  }

  async ngOnInit(): Promise<void> {
    this.ccService.statusChange$.subscribe((_) => this.hasConsentedToCookies());
    if (await this.isLoggedIn()) {
      logUtishaInfo('User is already logged in');
      try {
        this.user = await this.userServiceFrontend.getUser();
      } catch (e) {}
    }
    this.email = this.cookieService.getCookieValue('loginEmail');
    this.campaign = await this.utishaGeneralStore.getCampaign();
    const slug = localStorage.getItem('utisha_last_school');
    if (slug) {
      this.tenantService.resolve(slug).subscribe({
        next: org => { this.org = org; this.loading = false; },
        error: () => { this.loading = false; },
      });
    } else {
      this.loading = false;
    }

    if (this.user && this.page !== 'reset-password') {
      this.gotoPage('logged-in');
      return;
    }
    forkJoin([
      this.htmlDocSevice.getHtmlDocumentByType(
        HtmlDocumentDto.TypeEnum.WebsiteTermsConditions,
      ),
      this.htmlDocSevice.getHtmlDocumentByType(
        HtmlDocumentDto.TypeEnum.Privacy,
      ),
    ]).subscribe(([conditionDocuments, privacyStatements]) => {
      if (conditionDocuments.length >= 0) {
        this.conditionsStatement = conditionDocuments[0] as HtmlDocumentDto;
      }
      if (privacyStatements.length >= 0) {
        this.privacyStatement = privacyStatements[0] as HtmlDocumentDto;
      }
    });
  }

  private async isLoggedIn(): Promise<boolean> {
    try {
      await getCurrentUser();
    } catch (e) {
      return false;
    }
    return true;
  }

  ngAfterViewInit() {
    this.templates.forEach((directive) => {
      this.templateMap.set(directive.name, directive.templateRef);
    });
    this.pageTemplate = this.getPageTemplate(this.page);
  }

  getPageTemplate(page: PageType): TemplateRef<any> | undefined {
    let pageTemplateName;
    switch (page) {
      case 'email':
      case 'account-created':
        pageTemplateName = 'enterEmail';
        break;
      case 'existing-user-password':
      case 'new-user-password':
        pageTemplateName = 'enterPassword';
        break;
      default:
        pageTemplateName = camelize(page);
    }
    return this.templateMap.get(pageTemplateName);
  }

  private hasConsentedToCookies(): boolean {
    if (!this.ccService.hasConsented()) {
      this.error = 'login.cookie error';
      this.ccService.fadeIn();
      return false;
    }
    this.error = undefined;
    return true;
  }
  async handleSelectedLoginMethod(provider: UtishaAuthProvider) {
    if (!this.hasConsentedToCookies()) {
      return;
    }
    if (provider === 'Cognito') {
      this.provider = provider;
      this.gotoPage('email');
      this.changeDetector.detectChanges();
    } else {
      await this.userServiceFrontend.signin({ provider });
    }
  }

  get title() {
    if (this.selectedConditionsDocument) {
      return this.translate.instant(
        'document.type.' + this.selectedConditionsDocument.type,
      );
    }
    if (!this.provider) {
      return 'login.title';
    }
    if (!this.email) {
      return 'login.continue with email';
    }
  }

  get back() {
    switch (this.page) {
      case 'auth-method':
      case 'logged-in':
        return;
      case 'email':
        return () => this.gotoPage('auth-method');
      case 'existing-user-password':
        return () => this.gotoPage('email');
      case 'new-user-password':
        return () => this.gotoPage('signup');
      case 'signup':
        return () => this.gotoPage('email');
      case 'document':
        return () => this.gotoPage('auth-method');
      case 'forgot-password-submitted':
        return () => this.gotoPage('existing-user-password');
      default:
        return () => this.gotoPage('auth-method');
    }
  }

  titleFor(doc: HtmlDocumentDto): string {
    // Return the raw translation key — the `titleTemplate` below pipes
    // `customTitle` through `| translate`, so resolving here too would
    // double-lookup and render blank.
    return 'document.type.' + doc.type;
  }

  contentsFor(doc: HtmlDocumentDto): string | undefined {
    const translation = doc.translations
      ? this.languageService.getTranslation(doc.translations)
      : undefined;
    return (translation || {}).contents;
  }

  get showConditionsDocument() {
    return typeof this.selectedConditionsDocument !== 'undefined';
  }

  showDoc(doc: HtmlDocumentDto) {
    this.selectedConditionsDocument = doc;
    this.page = 'document';
    return false;
  }

  gotoPage(page: PageType) {
    this.error = undefined;
    this.page = page;
    this.pageTemplate = this.getPageTemplate(page);
  }

  private validateEmail(): boolean {
    return !!this.email?.trim().match(EMAIL_REGEX);
  }

  submitEmail() {
    if (this.validateEmail()) {
      this.cookieService.setCookie({ name: 'loginEmail', value: this.email! });
      this.authService.userWithEmailExists(this.email!).subscribe({
        next: (exists) => {
          if (exists) {
            this.gotoPage('existing-user-password');
          } else {
            this.gotoPage('signup');
          }
        },
        error: () => {
          this.error = 'login.error';
        },
      });
    } else {
      this.error = 'login.invalid email';
    }
  }

  async submitPassword() {
    this.loading = true;
    if (this.isPasswordValid()) {
      if (this.page === 'existing-user-password') {
        await this.signinWithEmail();
      } else {
        logUtishaInfo('signing up');
        await this.signup();
      }
    } else {
      this.error = 'login.invalid password';
      this.loading = false;
    }
  }

  isPasswordValid() {
    return (this.password ?? '').length >= 8;
  }

  private async signinWithEmail() {
    if (this.validateCredentials()) {
      await this.userServiceFrontend
        .signin({
          provider: 'Cognito',
          credentials: {
            username: this.email!,
            password: this.password!,
          },
        })
        .then(() => {
          this.loading = false;
          this.nav.home();
        })
        .catch((e) => {
          this.loading = false;
          logUtishaError(e);
          if (e.code === 'UserNotConfirmedException') {
            this.gotoPage('unconfirmed');
          } else {
            this.error = e.message;
          }
        });
    }
  }

  private async signup() {
    this.userServiceFrontend.needRefresh = true;
    try {
      await this.userServiceFrontend.signup({
        email: this.email!,
        name: this.name,
        password: this.password!,
        campaign: this.campaign,
      });
      this.gotoPage('signup-success');
      this.loading = false;
    } catch (error: any) {
      this.loading = false;
      this.error = tryResolveCognitoI18nError(error.toString());
    }
  }

  private validateCredentials() {
    if (!this.email || !this.password) {
      logUtishaDebug('Invalid credentials.');
      this.error = 'login.invalid credentials';
      this.loading = false;
      return false;
    }
    return true;
  }

  submitName() {
    if ((this.name ?? '').length >= 2) {
      this.gotoPage('new-user-password');
    } else {
      this.error = 'login.invalid name';
    }
  }

  finishSignup() {
    this.password = undefined;
    this.gotoPage('existing-user-password');
  }

  async submitForgotPassword() {
    if (this.validateEmail()) {
      try {
        logUtishaInfo(`Resetting password for ${this.email}`);
        await resetPassword({ username: this.email! });
        this.gotoPage('forgot-password-submitted');
      } catch (err: any) {
        logUtishaError(err);
        this.error = err.message;
      }
    } else {
      this.error = 'login.invalid email';
    }
  }

  gotoForgotPassword() {
    this.gotoPage('forgot-password');
    return false;
  }

  submitResetPassword() {}

  private clearQueryParams() {
    // Get the current path without query params
    const basePath = window.location.pathname;

    // Update the URL to remove query params without reloading
    window.history.replaceState({}, document.title, basePath);
  }

  handlePasswordChanged() {
    this.clearQueryParams();
    if (this.user) {
      this.nav.profile(this.user);
    } else {
      this.gotoPage('existing-user-password');
    }
  }

  async continueToWebsite(): Promise<void> {
    if (!this.user) {
      this.nav.home();
      return;
    }
    if (this.user.userType === UserTypeEnum.Teacher) {
      const isAdmin = await this.userServiceFrontend.isAdmin();
      isAdmin ? this.nav.defaultAdminPage() : this.nav.defaultTeacherPage();
    } else {
      this.nav.defaultStudentPage();
    }
  }

  async logout() {
    await this.userServiceFrontend.logout();
    this.gotoPage('auth-method');
  }

  cancelPasswordChange() {
    if (this.user) {
      this.nav.profile(this.user);
    } else {
      this.gotoPage('auth-method');
    }
  }

  async resendConfirmationMail() {
    if (!this.email) {
      this.error = 'login.unconfirmed.no-email';
      return;
    }
    try {
      await firstValueFrom(
        this.authService.resendConfirmation({ email: this.email }),
      );
      this.messageService.success('login.unconfirmed.success');
    } catch (e) {
      this.messageService.apiError(e);
    }
  }
}
